/**
 * Team (katalog s. 19) — herkes kendi girişiyle.
 *
 * Üyeler firm_team() ile (e-postalar auth.users'ta; yalnız sahip/yönetici),
 * bekleyen davetler firm_staff_invites (RLS). Davet Worker /api/team/invite:
 * create_staff_invite kullanıcının kendi oturumuyla çalışır, e-posta Resend ile.
 * Çıkarma remove_firm_member, davet iptali revoke_staff_invite. Kurallar
 * (yönetici yalnız personel davet eder ve çıkarır) veritabanında.
 *
 * AI kredisi (katalog s. 27): personel ofis havuzundan harcar, her üyenin aylık
 * sınırını sahip koyar (yönetici yalnız personelinkini). Sınır yoksa 0: yeni
 * üye sınır verilene kadar AI kullanamaz. Kural credit_set_member_cap içinde.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';

interface Member { member_id: string; role: string; status: string; email: string; name: string | null;
  joined_at: string; assigned_clients: number | null; last_active: string | null }
interface Invite { id: string; email: string; role: string; expires_at: string; created_at: string }

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
function ago(iso: string | null): string {
  if (!iso) return '—';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 300) return 'now';
  if (s < 3600) return Math.floor(s / 60) + ' min ago';
  if (s < 86400) return Math.floor(s / 3600) + ' h ago';
  return Math.floor(s / 86400) + ' d ago';
}
function myRole(): string { return (globalThis as any).navFirmContext?.()?.role ?? 'owner'; }
function firmId(): string | null { return (globalThis as any).navFirmContext?.()?.firm_owner_id ?? null; }
interface Pool { ok: boolean; available: number; allowance_left: number; purchased: number;
  members: Array<{ member_id: string; role: string; cap: number; used: number }> }

async function render(): Promise<void> {
  const root = el('ft-root');
  const sb = legacy.sb;
  if (!root || !sb) return;
  const role = myRole();
  if (role !== 'owner' && role !== 'manager') {
    root.innerHTML = '<div class="emp">Only the firm owner or a manager can manage the team.</div>';
    return;
  }
  root.innerHTML = '<div class="emp">…</div>';
  const firm = firmId();
  const [m, i, p] = await Promise.all([
    sb.rpc('firm_team'),
    sb.from('firm_staff_invites').select('id,email,role,expires_at,created_at')
      .is('used_by', null).is('revoked_at', null).gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false }),
    firm ? sb.rpc('credit_pool_summary', { p_firm: firm }) : Promise.resolve(null),
  ]);
  const pool = (p && p.data) as Pool | null;
  const caps = new Map((pool?.members ?? []).map((x) => [x.member_id, x]));
  const members: Member[] = ((m && m.data) || []).filter((x: Member) => x.status === 'active');
  const invites: Invite[] = (i && i.data) || [];

  const row = (x: Member) => {
    const canRemove = x.role !== 'owner' && (role === 'owner' || x.role === 'staff');
    return `<div class="card" style="margin-bottom:8px;display:flex;flex-wrap:wrap;gap:8px 14px;align-items:center">`
      + `<div class="av">${esc((x.name || x.email || '?').charAt(0).toUpperCase())}</div>`
      + `<div style="flex:2 1 180px;min-width:0"><div style="font-weight:600">${esc(x.name || x.email)}</div>`
      + `<div style="font-size:10px;color:var(--t2)" translate="no">${esc(x.email)}</div></div>`
      + `<div style="flex:0 0 80px;font-size:12px;text-transform:capitalize">${esc(x.role)}</div>`
      + `<div style="flex:0 0 80px;font-size:12px">${x.role === 'owner' || x.role === 'manager' ? 'All clients' : esc(x.assigned_clients ?? 0) + ' clients'}</div>`
      + `<div style="flex:0 0 80px;font-size:11px;color:var(--t2)">${esc(ago(x.last_active))}</div>`
      + (canRemove ? `<button class="btn bg bsm" onclick="ftRemove('${esc(x.member_id)}')">Remove</button>` : '<span style="width:60px"></span>')
      + capRow(x)
      + `</div>`;
  };
  /* Kendi sınırını kimse değiştiremez; yönetici yalnız personelinkini. */
  const capRow = (x: Member) => {
    const c = caps.get(x.member_id);
    if (!pool?.ok || x.role === 'owner' || !c) return '';
    const editable = role === 'owner' || x.role === 'staff';
    return `<div style="flex:1 1 100%;display:flex;gap:8px;align-items:center;font-size:11px;color:var(--t2)">`
      + `<span>AI credits this month: <b translate="no">${esc(c.used)}</b> used of</span>`
      + (editable
        ? `<input class="inp" type="number" min="0" max="1000000" step="1" value="${esc(c.cap)}" data-ft-cap="${esc(x.member_id)}" style="flex:0 0 90px;padding:4px 8px;font-size:12px">`
          + `<button class="btn bg bsm" onclick="ftSetCap('${esc(x.member_id)}')">Save limit</button>`
        : `<b translate="no">${esc(c.cap)}</b>`)
      + (c.cap === 0 ? '<span>· no AI until a limit is set</span>' : '')
      + `</div>`;
  };
  const inv = (x: Invite) => {
    const days = Math.max(0, Math.ceil((new Date(x.expires_at).getTime() - Date.now()) / 864e5));
    return `<div class="card" style="margin-bottom:8px;display:flex;flex-wrap:wrap;gap:8px 14px;align-items:center;border-style:dashed">`
      + `<div class="av">${esc(x.email.charAt(0).toUpperCase())}</div>`
      + `<div style="flex:2 1 180px"><div style="font-weight:600" translate="no">${esc(x.email)}</div>`
      + `<div style="font-size:10px;color:var(--t2)">Invite sent · expires in ${days} day${days === 1 ? '' : 's'}</div></div>`
      + `<div style="flex:0 0 80px;font-size:12px;text-transform:capitalize">${esc(x.role)}</div>`
      + `<button class="btn bg bsm" onclick="ftInvite('${esc(x.email)}','${esc(x.role)}')">Resend</button>`
      + `<button class="btn bg bsm" onclick="ftRevoke('${esc(x.id)}')">Revoke</button></div>`;
  };

  root.innerHTML = `<div class="pt">Team</div>`
    + `<div class="ps">${members.length} member${members.length === 1 ? '' : 's'}${invites.length ? ' · ' + invites.length + ' invite' + (invites.length === 1 ? '' : 's') + ' pending' : ''}. Everyone signs in with their own email; staff only see the clients assigned to them.</div>`
    + (pool?.ok ? `<div class="card" style="margin-bottom:14px;display:flex;gap:14px;flex-wrap:wrap;align-items:center">`
      + `<div style="flex:1 1 200px"><div class="set" style="margin-bottom:4px">Firm AI credits</div>`
      + `<div style="font-size:12px;color:var(--t2)">Everyone on the team spends from this pool, up to the monthly limit you set for them.</div></div>`
      + `<div style="font-size:20px;font-weight:800" translate="no">${esc(pool.available)}</div>`
      + `<button class="btn bg bsm" onclick="go('v7','ni7')">Top up</button></div>` : '')
    + `<div class="card" style="margin-bottom:14px"><div class="set">Invite staff</div>`
    + `<div style="display:flex;gap:8px;flex-wrap:wrap">`
    + `<input class="inp" id="ft-email" type="email" placeholder="name@yourfirm.com" style="flex:2 1 200px">`
    + `<select class="inp" id="ft-role" style="flex:0 0 130px"><option value="staff">Staff</option>${role === 'owner' ? '<option value="manager">Manager</option>' : ''}</select>`
    + `<button class="btn br" onclick="ftInvite()">Send invite</button></div>`
    + `<div style="font-size:10px;color:var(--t2);margin-top:6px">The link works for 7 days and only for this email address. Managers see every client; staff see the clients you assign to them.</div></div>`
    + members.map(row).join('') + invites.map(inv).join('');
}

async function invite(email?: unknown, roleArg?: unknown): Promise<void> {
  const e = typeof email === 'string' ? email : ((el('ft-email') as HTMLInputElement | null)?.value ?? '');
  const r = typeof roleArg === 'string' ? roleArg : ((el('ft-role') as HTMLSelectElement | null)?.value ?? 'staff');
  if (!e.trim()) { legacy.toast('Enter an email address.'); return; }
  try {
    const res = await legacy.postApi('/api/team/invite', { email: e.trim(), role: r });
    const d = await res.json().catch(() => ({})) as { ok?: boolean; error?: string; email_sent?: boolean };
    if (!res.ok || d.ok !== true) { legacy.toast(d.error || 'The invite could not be sent.'); return; }
    legacy.toast(d.email_sent ? 'Invite sent.' : 'Invite created, but the email could not be sent.');
  } catch {
    legacy.toast('The invite could not be sent.');
  }
  void render();
}

export default defineFeature({
  id: 'firm-team',
  label: 'Ofis ekibi',
  view: 'v11',
  mount: () => { void render(); },
  legacyGlobals: {
    ftInvite: (email?: unknown, role?: unknown) => invite(email, role),
    ftRemove: async (id: unknown) => {
      if (!confirm('Remove this person from the team? Their access ends immediately.')) return;
      const r = await legacy.sb?.rpc('remove_firm_member', { p_member_id: String(id) });
      legacy.toast(r?.data?.ok ? 'Removed from the team.' : (r?.data?.error ?? 'Could not remove this member.'));
      void render();
    },
    ftSetCap: async (id: unknown) => {
      const firm = firmId();
      const input = document.querySelector<HTMLInputElement>(`[data-ft-cap="${CSS.escape(String(id))}"]`);
      const limit = Number(input?.value);
      if (!firm || !Number.isInteger(limit) || limit < 0 || limit > 1000000) { legacy.toast('Enter a whole number of credits.'); return; }
      const r = await legacy.sb?.rpc('credit_set_member_cap', { p_firm: firm, p_member: String(id), p_limit: limit });
      legacy.toast(r?.data?.ok ? 'Monthly AI limit saved.' : (r?.error ? 'You cannot change this limit.' : 'The limit could not be saved.'));
      void render();
    },
    ftRevoke: async (id: unknown) => {
      const r = await legacy.sb?.rpc('revoke_staff_invite', { p_invite_id: String(id) });
      legacy.toast(r?.data?.ok ? 'Invite revoked.' : (r?.data?.error ?? 'Could not revoke the invite.'));
      void render();
    },
  },
});
